import { AutocompleteInputChangeReason, ListItem } from "@mui/material";
import { SxProps } from "@mui/system";
import { SyntheticEvent, useCallback, useState } from "react";
import { NameIdOption } from "../../../../../common/types/commonTypes";
import AutocompleteBase from "../autocompleteBase/AutocompleteBase";
import { handleAutocompleteChange } from "../helpers";
import useCertificatesAutocomplete from "./useCertificatesAutocomplete";

interface Props {
  value?: NameIdOption[];
  onChange?: (certificates: NameIdOption[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  limitTags?: number;
  sx?: SxProps;
}

const CertificatesAutocomplete = ({
  value,
  onChange,
  multiple = true,
  disabled = false,
  fullWidth = true,
  size = "small",
  limitTags,
  sx = {},
}: Props) => {
  const [inputValue, setInputValue] = useState("");
  const { certificateOptions, isLoading } = useCertificatesAutocomplete();

  const handleChange = useCallback(
    (newValue: NameIdOption | NameIdOption[] | null) => {
      setInputValue("");
      handleAutocompleteChange(newValue, onChange, multiple);
    },
    [onChange, multiple],
  );

  const handleInputChange = (
    _: SyntheticEvent,
    newValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "reset") {
      return;
    }
    setInputValue(newValue);
  };

  const getOptionLabel = (option: NameIdOption) => option.name;
  const isOptionEqualToValue = (option: NameIdOption, val: NameIdOption) =>
    option.id === val.id;

  return (
    <AutocompleteBase<NameIdOption>
      value={value || []}
      onChange={(newValue) => handleChange(newValue)}
      options={certificateOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      limitTags={limitTags}
      size={size}
      loading={isLoading}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      onClose={() => setInputValue("")}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          {option.name}
        </ListItem>
      )}
      sx={sx}
    />
  );
};

export default CertificatesAutocomplete;
